import Component from '@glimmer/component'
import type { TOC } from '@ember/component/template-only'
import { queryParam } from 'teacher-ui-client/decorators/query-param'
import type { CourseProgressHistoryReportData } from 'teacher-ui-client/components/reporting/writing-legends/course-progress/history/report-loader'
import {
  serialize as serializeSort,
  deserialize as deserializeSort,
  updateSortColumns,
} from '@blakeelearning/data-tables/utils/sorting'
import { action } from '@ember/object'
import type { SortingConfig } from '@blakeelearning/data-tables/utils/sorting'
import { getSortViewModel } from 'teacher-ui-client/utils/sorting'
import { t } from 'ember-intl'
import { on } from '@ember/modifier'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import formatValue from 'teacher-ui-client/helpers/format-value'

interface Signature {
  Element: HTMLTableElement
  Args: {
    isReloading?: boolean
    studentsReportData: CourseProgressHistoryReportData['students']
  }
}

export const CourseProgressHistoryTable: TOC<Signature> = <template>
  <table class="table-print-zoom-1/2 divide-dusty-black-200 relative table-fixed divide-y" ...attributes>
    <thead>
      <tr class="bg-white">
        <ThSort class="min-w-32" @sortKey="first_name" @text={{t "reporting.firstName"}} />
        <ThSort class="min-w-32" @sortKey="last_name" @text={{t "reporting.lastName"}} />
        <ThSort
          @sortKey="lessons_completed"
          @text={{t "reporting.charts.courseProgress.lessonsCompleted" htmlSafe=true}}
        />
        <ThSort
          @sortKey="max_lesson"
          @text={{t "reporting.charts.courseProgress.highestLessonCompleted" htmlSafe=true}}
        />
      </tr>
    </thead>
    <tbody class="divide-dusty-black-200 divide-y {{if @isReloading 'opacity-50'}}">
      {{#each @studentsReportData as |student index|}}
        <tr class="bg-white" data-test-table-row="{{index}}">
          <td class="truncate p-3" data-test-cell="first_name">
            {{student.first_name}}
          </td>
          <td class="truncate p-3" data-test-cell="last_name">
            {{student.last_name}}
          </td>
          <td class="p-3" data-test-cell="lessons_completed">
            {{formatValue student.lessons_completed}}
          </td>
          <td class="p-3" data-test-cell="max_lesson">
            {{formatValue student.max_lesson}}
          </td>
        </tr>
      {{/each}}
    </tbody>
  </table>
</template>

interface ThSortSignature {
  Element: HTMLTableCellElement
  Args: {
    text?: string
    sortKey: string
  }
}

class ThSort extends Component<ThSortSignature> {
  @queryParam('reporting/ui-scope/writing-legends/course-progress') sort!: string

  get sortingConfig() {
    return deserializeSort(this.sort)
  }

  set sortingConfig(newSortConfig: SortingConfig) {
    this.sort = serializeSort(newSortConfig)
  }

  @action
  updateSort() {
    this.sortingConfig = updateSortColumns(this.sortingConfig, this.args.sortKey)
  }

  get sortViewModel() {
    return getSortViewModel(this.sortingConfig, this.args.sortKey)
  }

  <template>
    <th
      class="h-[80px] select-none"
      data-test-sort-key={{@sortKey}}
      aria-sort={{this.sortViewModel.ariaDirection}}
      ...attributes
    >
      <button
        type="button"
        class="flex h-full w-full items-center gap-x-3 p-3 text-left {{if @text 'justify-between' 'justify-end'}}"
        {{on "click" this.updateSort}}
      >
        {{#if @text}}<span class="text-dusty-black-250 font-normal uppercase">{{@text}}</span>{{/if}}
        <PhosphorIcon @icon={{this.sortViewModel.icon}} class="text-dusty-black-400 print:hidden" />
      </button>
    </th>
  </template>
}
